<template>
  <div>
    <div class="add-ad" v-if="!isLoading">
      <a
        class="button is-primary"
        :disabled="isAddingAd"
        @click="addAd"
        :class="{ 'is-loading': isAddingAd }"
      >
        Create new ad
      </a>
    </div>

    <Spinner v-if="isLoading" />

    <div class="notification is-danger" v-if="addAdError">
      {{ addAdError }}
    </div>

    <div class="notification is-danger" v-if="loadError">{{ loadError }}</div>

    <!-- Ads list -->
    <div class="panel" v-if="showAdsList">
      <a
        class="panel-block animal-list-row"
        v-for="ad in filteredAds"
        :key="ad.id"
        @click="adSelected(ad)"
        :class="{
          'has-background-grey-dark': isSelectedAd(ad),
          'has-text-light': isSelectedAd(ad)
        }"
      >
        <div class="title-block">{{ ad.title }}</div>

        <div class="is-size-7">{{ subtitle(ad) }}</div>

        <span class="period-tag has-text-weight-bold" :class="periodStatusTagClass(ad)">{{
          periodStatusTagText(ad)
        }}</span>
      </a>
    </div>
  </div>
</template>

<script>
import Spinner from '@/components/utils/Spinner'

import {
  LOAD_ADS,
  LOAD_ADS_META_DATA,
  IS_LOADING_ADS,
  IS_LOADING_ADS_META_DATA,
  GET_ADS_LOAD_ERROR,
  GET_ADS_LOAD_RESULT,
  GET_ADS_META_DATA_LOAD_RESULT,
  SELECT_AD_WITH_ID,
  GET_SELECTED_AD,
  SAVE_AD
} from '@/modules/ads/adsStore'

export default {
  name: 'AdsSidebar',
  components: {
    Spinner
  },

  props: {
    adsStorePath: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isAddingAd: false,
      addAdError: null

      // categoryFilter: '',
      // filterInput: '',
      // isAddingAnimal: false,
      // addAnimalError: null
    }
  },

  mounted() {
    if (this.$route.name === 'adsSidebar') {
      const setSelectedAdIdAction = `${this.adsStorePath}/${SELECT_AD_WITH_ID}`
      this.$store.dispatch(setSelectedAdIdAction, { adId: null })
    }

    if (!this.ads) {
      this.loadAds()
    }

    if (!this.adsMetaData) {
      this.loadAdsMetaData()
    }

    this.categoryFilter = this.$route.query.category || ''
    this.filterInput = this.$route.query.filter || ''
  },

  methods: {
    loadAds() {
      const loadAdsAction = `${this.adsStorePath}/${LOAD_ADS}`
      this.$store.dispatch(loadAdsAction)
    },

    loadAdsMetaData() {
      const loadAdsMetaDataAction = `${this.adsStorePath}/${LOAD_ADS_META_DATA}`
      this.$store.dispatch(loadAdsMetaDataAction)
    },

    isSelectedAd(ad) {
      const getSelectedAd = `${this.adsStorePath}/${GET_SELECTED_AD}`
      const selectedAd = this.$store.getters[getSelectedAd]

      if (!selectedAd) {
        return false
      }

      return selectedAd.id === ad.id
    },

    adSelected(ad) {
      this.showAdDetails(ad.id)
    },

    showAdDetails(adId) {
      this.$router.push({
        name: 'adDetails',
        params: { adId }
      })
    },

    async addAd() {
      if (!this.adsMetaData) {
        return
      }

      this.isAddingAd = true
      this.addAdError = null

      const ad = {
        adTypeId: this.adsMetaData.types[0].id,
        customerId: this.adsMetaData.customers[0].id,
        targetURL: 'http://example.com',
        title: '[new ad]'
      }

      try {
        const saveAdAction = `${this.adsStorePath}/${SAVE_AD}`
        const response = await this.$store.dispatch(saveAdAction, { ad })

        const adId = response.data.id
        this.showAdDetails(adId)

        this.loadAds()
      } catch (error) {
        this.addAdError = error || 'Something went wrong.'
      } finally {
        this.isAddingAd = false
      }
    },

    periodStatusTagText(ad) {
      if (this.hasActivePeriod(ad)) {
        return 'Active'
      }

      if (this.hasPlannedPeriod(ad)) {
        return 'Planned'
      }

      return 'Not active'
    },

    periodStatusTagClass(ad) {
      const isActive = this.hasActivePeriod(ad)
      const isPlanned = this.hasPlannedPeriod(ad)

      return {
        'has-background-success': isActive,
        'has-text-light': isActive || isPlanned,
        'has-background-warning': isPlanned && !isActive,
        'has-background-grey-lighter': !isActive && !isPlanned
      }
    },

    hasActivePeriod(ad) {
      if (!this.adsMetaData) {
        return false
      }

      const adId = ad.id
      return this.adsMetaData.activePeriods.find(period => period.adId === adId)
    },

    hasPlannedPeriod(ad) {
      if (!this.adsMetaData) {
        return false
      }

      const adId = ad.id
      return this.adsMetaData.plannedPeriods.find(period => period.adId === adId)
    },

    subtitle(ad) {
      if (!this.adsMetaData) {
        return ''
      }

      const customer = this.adsMetaData.customers.find(customer => customer.id === ad.customerId)
      const customerName = customer.name || ''

      const adType = this.adsMetaData.types.find(type => type.id === ad.adTypeId)
      const adTypeName = adType.name || ''

      return `${customerName} - ${adTypeName}`
    }
  },

  computed: {
    isLoading() {
      const isLoadingAds = `${this.adsStorePath}/${IS_LOADING_ADS}`
      const isLoadingAdsMetaData = `${this.adsStorePath}/${IS_LOADING_ADS_META_DATA}`
      return this.$store.getters[isLoadingAds] || this.$store.getters[isLoadingAdsMetaData]
    },

    showAdsList() {
      if (!this.ads) {
        return false
      }

      if (!this.adsMetaData) {
        return false
      }

      return this.ads.length > 0
    },

    ads() {
      const getAds = `${this.adsStorePath}/${GET_ADS_LOAD_RESULT}`
      return this.$store.getters[getAds]
    },

    adsMetaData() {
      const getAdsMetaData = `${this.adsStorePath}/${GET_ADS_META_DATA_LOAD_RESULT}`
      return this.$store.getters[getAdsMetaData]
    },

    filteredAds() {
      if (!this.ads) {
        return null
      }

      var filteredAds = [...this.ads]

      return filteredAds
    },

    loadError() {
      const getLoadError = `${this.adsStorePath}/${GET_ADS_LOAD_ERROR}`
      return this.$store.getters[getLoadError]
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/style.scss';

.select {
  width: 100%;
}

select {
  width: 100%;
}

form {
  padding-bottom: $column-gap;
}

.animal-list-row {
  min-height: 41px;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
}

.panel {
  margin-bottom: 2px !important;
}

.panel-block {
  position: relative;

  .title-block {
    margin-top: 0.5em;
  }

  .period-tag {
    position: absolute;
    left: 0px;
    top: 0px;
    font-size: 9px;
    padding: 0px 2px;
  }
}

.add-ad {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-bottom: 1em;
}

.table-footer {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;

  span {
    color: $grey;
    font-size: 0.9rem;
  }
}
</style>
